import { http, HttpResponse } from '@src/http/utility';
import { environment } from '../../environments/environment';

/* -------------------------------------------------------------------------- */
/*                               HTTP Constants                               */
/* -------------------------------------------------------------------------- */
enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

let groupMemberCode = '';
let role = '';

export function setGroupMemberCode(code: string) {
  groupMemberCode = code;
}

export function setRole(code: string) {
  code = code.replace(/\d+$/, '');
  if (code === 'VCP') {
    code = 'DLR';
  }
  role = code;
}

/* -------------------------------------------------------------------------- */
/*                             Public HTTP Methods                            */
/* -------------------------------------------------------------------------- */

export async function get<T>(
  uri: string,
  args: RequestInit = {},
  isPythonWebApi = false,
): Promise<HttpResponse<T>> {
  const updatedUri = appendQueryParams(uri, { groupMemberCode, role });
  return safeHttp<T>(
    await buildRequest(updatedUri, HttpMethod.GET, args, isPythonWebApi),
  );
}

export async function post<T>(
  uri: string,
  body: any,
  args: RequestInit = {},
  isPythonWebApi = false,
): Promise<HttpResponse<T>> {
  const bodyParams = appendCommonBodyParams(body);
  return safeHttp<T>(
    await buildRequest(uri, HttpMethod.POST, args, isPythonWebApi, bodyParams),
  );
}

export async function put<T>(
  uri: string,
  body: any,
  args: RequestInit = {},
): Promise<HttpResponse<T>> {
  const bodyParams = appendCommonBodyParams(body);
  return safeHttp<T>(
    await buildRequest(uri, HttpMethod.PUT, args, false, bodyParams),
  );
}

export async function patch<T>(
  uri: string,
  body: any,
  args: RequestInit = {},
): Promise<HttpResponse<T>> {
  const bodyParams = appendCommonBodyParams(body);
  return safeHttp<T>(
    await buildRequest(uri, HttpMethod.PATCH, args, false, bodyParams),
  );
}

export async function remove<T>(
  uri: string,
  args: RequestInit = {},
): Promise<HttpResponse<T>> {
  return safeHttp<T>(await buildRequest(uri, HttpMethod.DELETE, args));
}

export async function deleteRequest<T>(
  uri: string,
  args: RequestInit = {},
): Promise<HttpResponse<T>> {
  const updatedUri = appendQueryParams(uri, { groupMemberCode, role });
  return safeHttp<T>(await buildRequest(updatedUri, HttpMethod.DELETE, args));
}

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function appendQueryParams(
  url: string,
  params: Record<string, string>,
  condition = true,
): string {
  if (!condition) return url;

  const urlObj = new URL(url, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value) urlObj.searchParams.set(key, value);
  });
  return urlObj.toString();
}

function validateUri(uri: string): void {
  if (!uri) throw new Error('Invalid request: URL cannot be empty.');
}

function appendCommonBodyParams(body: any): any {
  return { ...body, groupMemberCode, role };
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

/* -------------------------------------------------------------------------- */
/*                              Token Management                              */
/* -------------------------------------------------------------------------- */

async function ensureValidHeaders(
  existingHeaders: HeadersInit = {},
  isPythonWebApi = false,
): Promise<Headers> {
  const headers = new Headers(existingHeaders);

  if (!isLocalhost()) {
    const token = await getValidToken(isPythonWebApi);
    if (!token) {
      console.warn('Redirecting to login due to missing/expired token...');
      window.location.href = environment.redirectGroupUrl;
      return new Headers(); // redirect takes over
    }
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('X-APIM-Access', 'true');

  headers.set('Content-Type', 'application/json');
  return headers;
}

async function getValidToken(isPythonWebApi: boolean): Promise<string | null> {
  const key = isPythonWebApi ? 'pythonbearerToken' : 'dotnetbearerToken';
  const { token, azureTokenexpired } = await getStoredToken(key);

  if (!token || azureTokenexpired) {
    console.warn(
      !token
        ? `No token found for ${key}, redirecting to login...`
        : `Token for ${key} expired, redirecting to login...`,
    );
    window.location.href = environment.redirectGroupUrl;
    return null;
  }
  return token;
}

/* -------------------------------------------------------------------------- */
/*                              Save token method                             */
/* -------------------------------------------------------------------------- */
export function saveToken(
  key: string,
  token: string,
  expiresInSeconds: number,
): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = nowSeconds + expiresInSeconds; // ✅ absolute expiry timestamp

  sessionStorage.setItem(key, token);
  sessionStorage.setItem(`${key}_expiresAt`, expiresAt.toString());
}

/* -------------------------------------------------------------------------- */
/*                              Get token method                              */
/* -------------------------------------------------------------------------- */
export async function getStoredToken(key: string): Promise<{
  token: string | null;
  azureTokenexpired: boolean;
  minutesLeft: number;
}> {
  // console.log(`[TOKEN DEBUG] getStoredToken() called for key = ${key}`);

  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    // console.log(`[TOKEN DEBUG] No window/sessionStorage → returning expired`);
    return { token: null, azureTokenexpired: true, minutesLeft: 0 };
  }

  let token = sessionStorage.getItem(key);
  let expiresAtRaw = sessionStorage.getItem(`${key}_expiresAt`);
  // console.log(`[TOKEN DEBUG] Retrieved token =`, token ? 'FOUND' : 'NULL');
  // console.log(`[TOKEN DEBUG] Retrieved expiresAt =`, expiresAtRaw ?? 'NULL');

  if (!token || !expiresAtRaw) {
    // console.log(`[TOKEN DEBUG] Token or expiry missing → returning expired`);
    return { token: null, azureTokenexpired: true, minutesLeft: 0 };
  }

  let expirySeconds = Number(expiresAtRaw);
  let nowSeconds = Math.floor(Date.now() / 1000);
  let secondsLeft = expirySeconds - nowSeconds;
  let minutesLeft = Math.floor(secondsLeft / 60);
  let expired = secondsLeft <= 0;

  // console.log(
  //   `[TOKEN DEBUG] Token expires in ${secondsLeft}s (${minutesLeft}m). Expired? =`,
  //   expired,
  // );

  // ------------------------------------------
  // 1️⃣ If expired or about to expire (less than 5 min)
  // ------------------------------------------
  if (expired || minutesLeft < 5) {
    // console.log(
    //   `[TOKEN DEBUG] Token expired/about-to-expire → initiating refresh...`,
    // );

    await refreshAllTokens(); // async - but trigger

    // console.log(`[TOKEN DEBUG] Waiting for updated sessionStorage (no await)…`);

    // Read fresh values after refresh attempt
    token = sessionStorage.getItem(key);
    expiresAtRaw = sessionStorage.getItem(`${key}_expiresAt`);

    // console.log(`[TOKEN DEBUG] Post-refresh token =`, token ? 'FOUND' : 'NULL');
    // console.log(
    //   `[TOKEN DEBUG] Post-refresh expiresAt =`,
    //   expiresAtRaw ?? 'NULL',
    // );

    if (!token || !expiresAtRaw) {
      // console.log(`[TOKEN DEBUG] Refresh failed → clearing token from storage`);
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}_expiresAt`);
      return { token: null, azureTokenexpired: true, minutesLeft: 0 };
    }

    // Re-evaluate timing
    expirySeconds = Number(expiresAtRaw);
    nowSeconds = Math.floor(Date.now() / 1000);
    secondsLeft = expirySeconds - nowSeconds;
    minutesLeft = Math.floor(secondsLeft / 60);
    expired = secondsLeft <= 0;

    // console.log(
    //   `[TOKEN DEBUG] After refresh → secondsLeft = ${secondsLeft}, minutesLeft = ${minutesLeft}, expired? = ${expired}`,
    // );

    if (expired) {
      // console.log(
      //   `[TOKEN DEBUG] Token still expired → removing from sessionStorage`,
      // );
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}_expiresAt`);
      return { token: null, azureTokenexpired: true, minutesLeft: 0 };
    }

    // console.log(`[TOKEN DEBUG] Refresh successful → returning new valid token`);
    return { token, azureTokenexpired: false, minutesLeft };
  }

  // ------------------------------------------
  // 2️⃣ Valid token
  // ------------------------------------------
  // console.log(`[TOKEN DEBUG] Token valid → returning stored value`);
  return { token, azureTokenexpired: false, minutesLeft };
}

/* -------------------------------------------------------------------------- */
/*                         Unified Request Builder                            */
/* -------------------------------------------------------------------------- */

async function buildRequest(
  uri: string,
  method: HttpMethod,
  args: RequestInit,
  isPythonWebApi = false,
  body?: any,
): Promise<Request> {
  validateUri(uri);

  const headers = await ensureValidHeaders(args.headers || {}, isPythonWebApi);
  const requestArgs: RequestInit = {
    ...args,
    headers,
    method,
    body: body ? JSON.stringify(body) : args.body,
  };

  return new Request(uri, requestArgs);
}

/* -------------------------------------------------------------------------- */
/*                              HTTP Safety                                   */
/* -------------------------------------------------------------------------- */
async function safeHttp<T>(request: Request): Promise<HttpResponse<T>> {
  try {
    return await http<T>(request);
  } catch (error) {
    console.error(`HTTP ${request.method} ${request.url} failed:`, error);
    throw error;
  }
}

async function refreshAllTokens(): Promise<void> {
  try {
    console.log('[TOKEN REFRESH] Refreshing Azure DotNet and Python tokens...');

    const [dotnetRes, pythonRes] = await Promise.all([
      fetch('/api/azureDotNetAccessToken').then((r) => r.json()),
      fetch('/api/azurePythonAccessToken').then((r) => r.json()),
    ]);

    console.log('[TOKEN REFRESH] Responses:', { dotnetRes, pythonRes });

    // Save tokens with expiry
    if (dotnetRes.access_token)
      saveToken(
        'dotnetbearerToken',
        dotnetRes.access_token,
        dotnetRes.expires_in,
      );
    if (pythonRes.access_token)
      saveToken(
        'pythonbearerToken',
        pythonRes.access_token,
        pythonRes.expires_in,
      );

    console.log('[TOKEN REFRESH] Tokens updated in sessionStorage');
  } catch (err) {
    console.error('[TOKEN REFRESH] Failed to refresh tokens', err);
    // Optional: force redirect to login
    window.location.href = environment.redirectGroupUrl;
  }
}

function getFilenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const fnStar = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (fnStar?.[1]) {
    try {
      return decodeURIComponent(fnStar[1]);
    } catch {
      return fnStar[1];
    }
  }
  const fn = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (fn?.[1]) return fn[1];
  const fn2 = disposition.match(/filename\s*=\s*([^;]+)/i);
  if (fn2?.[1]) return fn2[1].trim();
  return null;
}
export async function downloadInstructionFile(
  uri: string,
  isPythonWebApi = false,
): Promise<void> {
  const finalUrl = appendQueryParams(uri, { groupMemberCode, role });

  const request = await buildRequest(
    finalUrl,
    HttpMethod.GET,
    {},
    isPythonWebApi,
  );

  const response = await fetch(request);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition');
  const headerFilename = getFilenameFromDisposition(disposition);
  const xFilename = response.headers.get('x-filename');
  const filename =
    headerFilename || xFilename || 'Input Sheet Instructions.doc';
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

export async function downloadZipReport(
  uri: string,
  queueId: number,
  isPythonWebApi = false,
) {
  const finalUrl = appendQueryParams(uri, {
    queueid: String(queueId),
  });

  const request = await buildRequest(
    finalUrl,
    HttpMethod.GET,
    {},
    isPythonWebApi,
  );
  const response = await fetch(request);

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const disposition = response.headers.get('content-disposition');
  const filename =
    disposition?.split('filename=')[1]?.replace(/"/g, '') || 'report.zip';

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(downloadUrl);
}
